import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield } from "@/components/Logo";
import { toast } from "sonner";
import { LogIn, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login, googleSession } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        setLoading(true);
        googleSession(sid).then(() => { window.location.hash = ""; nav("/"); })
          .catch(() => toast.error("Google sign-in failed")).finally(() => setLoading(false));
      }
    }
  }, []);

  useEffect(() => { if (user) nav("/"); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await login(email, password); toast.success("Welcome back"); nav("/"); }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  const google = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/login")}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-navy hero-grain text-white p-12 relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-brandred/20 blur-3xl" />
        <Shield size={52} withText />
        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 bg-brandred px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6">Built For You</div>
          <h1 className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight">
            Run the whole<br />company from<br /><span className="text-brandred">lead to cash.</span>
          </h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed">
            CRM, AI receptionist, estimating, proposals, production and accounting — one customer record, zero double entry.
          </p>
        </div>
        <p className="relative z-10 text-xs text-slate-400">An AI-powered roofing business management platform.</p>
      </div>

      <div className="flex items-center justify-center p-6 bg-cloud">
        <div className="w-full max-w-sm mb-card p-8 animate-scale-in">
          <div className="lg:hidden mb-6 flex justify-center"><Shield size={56} /></div>
          <h2 className="font-display text-3xl font-extrabold uppercase text-navy tracking-tight">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Masterbuilt Roofing · AI Business Manager</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-navy" />
            </div>
            <button data-testid="login-submit" disabled={loading}
              className="w-full bg-navy hover:bg-navy-deep text-white font-bold py-3 rounded-xl border-2 border-navy flex items-center justify-center gap-2 transition-all duration-150 active:translate-y-px disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Sign in
            </button>
          </form>
          <div className="my-4 flex items-center gap-3 text-xs text-slate-400"><div className="flex-1 h-px bg-slate-200" />OR<div className="flex-1 h-px bg-slate-200" /></div>
          <button data-testid="login-google" onClick={google}
            className="w-full bg-white hover:bg-slate-50 text-slate-800 font-semibold py-3 rounded-xl border-2 border-slate-300 flex items-center justify-center gap-2 transition-all">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-4 h-4" alt="" /> Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}
